import { Injectable, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, map, Observable, Subscription } from 'rxjs';
import * as CookieConsent from 'vanilla-cookieconsent';

export type CookieConsentCategory = 'necessary' | 'analytics';

interface CookieConsentData {
  categories?: string[];
}

@Injectable({
  providedIn: 'root',
})
export class CookieConsentService implements OnDestroy {
  consentState = new BehaviorSubject<string[]>([]);
  consentState$ = this.consentState.asObservable();
  langSub?: Subscription;

  constructor(private translate: TranslateService) {
    this.langSub = this.translate.onLangChange.subscribe((e) => {
      this.setLanguage(e.lang);
    });
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }

  init(translations: Record<string, () => Promise<CookieConsent.Translation>>, defaultLang: string) {
    CookieConsent.run({
      onFirstConsent: ({ cookie }) => this.updateState(cookie),

      categories: {
        necessary: { enabled: true, readOnly: true },
        analytics: {
          autoClear: {
            cookies: [{ name: /^_ga/ }, { name: '_gid' }],
          },
        },
      },
      language: {
        default: defaultLang,
        translations,
      },
    });

    const existing = CookieConsent.getCookie();
    if (existing?.categories) {
      this.consentState.next(existing.categories);
    }
  }

  setLanguage(lang: string) {
    CookieConsent.setLanguage(lang);
  }

  isCategoryEnabled$(category: CookieConsentCategory): Observable<boolean> {
    return this.consentState$.pipe(map((categories) => categories.includes(category)));
  }

  private updateState(cookie: CookieConsentData) {
    const categories = cookie?.categories || [];
    this.consentState.next(categories);
  }
}
